import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Phone, Check, MapPin, Percent, DollarSign, CalendarCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { cityHostConfig, cityHostJsonLd } from "@/data/cityHostPages";
import { cityHostConfigPt, cityHostJsonLdPt, cityHostAlternates, ptCityHostPath } from "@/data/cityHostPagesPt";
import { hubSlug, CITIES } from "@/data/cityLocations";
import { track } from "@/lib/analytics";

const ICONS = { Percent, DollarSign, CalendarCheck };

const TX = {
  en: { apply: "Apply to become a host", calc: "Calculate earnings", whyHost: "Why host in", localDemand: "Local rental demand in", whenRenters: "renters look for a car", faq: "Frequently asked questions", nearby: "Host in nearby Massachusetts cities", rentIn: "Rent out your car in", rentCarIn: "Rent a car in", allResources: "All hosting resources", ctaTitle: "List your", ctaTitle2: "car with Belo Rentals", ctaSub: "Set your price and keep your listed rate. Most complete applications are reviewed within 24 hours.", when: "When" },
  pt: { apply: "Candidate-se a anfitrião", calc: "Calcular ganhos", whyHost: "Por que hospedar em", localDemand: "Demanda local de aluguel em", whenRenters: "locatários procuram um carro", faq: "Perguntas frequentes", nearby: "Hospede em cidades próximas de Massachusetts", rentIn: "Alugue seu carro em", rentCarIn: "Alugue um carro em", allResources: "Todos os recursos para anfitriões", ctaTitle: "Anuncie seu carro de", ctaTitle2: "na Belo Rentals", ctaSub: "Defina o seu preço e receba a tarifa listada. A maioria das candidaturas completas é analisada em 24 horas.", when: "Quando" },
};

export default function CityHostPage({ citySlug, lang = "en" }) {
  const cfg = lang === "pt" ? cityHostConfigPt(citySlug) : cityHostConfig(citySlug);
  const [openFaq, setOpenFaq] = useState(0);
  if (!cfg) return null;
  const t = TX[lang] || TX.en;
  const alternates = cityHostAlternates(citySlug);
  const jsonLd = lang === "pt" ? cityHostJsonLdPt(cfg) : cityHostJsonLd(cfg);
  const nearbyPath = (n) => (lang === "pt" ? ptCityHostPath(n) : `/become-a-host/${n}-ma`);

  return (
    <div data-testid={`city-host-${lang}-${citySlug}`} className="bg-white">
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={cfg.path} keywords={cfg.keywords} alternates={alternates} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 sm:px-8 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.22),transparent)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/15 text-blue-200 text-sm font-medium mb-6"><MapPin size={14} /> {cfg.badge}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{cfg.h1}</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">{cfg.intro}</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => track("application_started", { page: `city_host_${citySlug}` })} data-testid="city-host-apply" className="px-7 py-3.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2">{t.apply} <ChevronRight size={18} /></Link>
            <Link to="/become-a-host#calculator" className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors">{t.calc}</Link>
            <a href="tel:+19782211451" onClick={() => track("host_phone_click", { page: `city_host_${citySlug}` })} className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-3 gap-5">
          {cfg.highlights.map((h, i) => {
            const Ic = ICONS[h.icon];
            return (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4">{Ic ? <Ic size={20} /> : null}</span>
                <h3 className="font-heading font-bold text-slate-900">{h.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why host here + local demand */}
      <section className="pb-8 px-5 sm:px-8 max-w-3xl mx-auto space-y-8">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">{t.whyHost} {cfg.cityName}</h2>
          <p className="text-slate-600 mt-3 leading-relaxed">{cfg.localIntro}</p>
        </div>
        {cfg.demand.length > 0 && (
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{t.localDemand} {cfg.cityName}</h2>
            <ul className="mt-4 space-y-2.5" data-testid="city-host-demand">
              {cfg.demand.map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed"><MapPin size={17} className="text-blue-500 shrink-0 mt-0.5" /> <span>{d}</span></li>
              ))}
            </ul>
          </div>
        )}
        {cfg.useCases.length > 0 && (
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{t.when} {cfg.cityName} {t.whenRenters}</h2>
            <ul className="mt-4 space-y-2.5" data-testid="city-host-usecases">
              {cfg.useCases.map((u, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed"><Check size={17} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{u}</span></li>
              ))}
            </ul>
          </div>
        )}
        <p className="flex items-start gap-2 text-slate-700 text-sm bg-amber-50 border border-amber-100 rounded-xl p-4">
          <Check size={16} className="text-amber-600 mt-0.5 shrink-0" /> {cfg.insurance}
        </p>
        <p className="text-xs text-slate-400">{cfg.trademark}</p>
      </section>

      {/* FAQ */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">{t.faq}</h2>
          <div className="mt-10 space-y-3">
            {cfg.faqs.map((f, i) => (
              <div key={i} data-testid={`city-host-faq-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities + hub */}
      <section className="py-14 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">{t.nearby}</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="city-host-nearby">
          {cfg.nearby.map((n) => (
            <Link key={n} to={nearbyPath(n)} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">{t.rentIn} {CITIES[n].cityName}</Link>
          ))}
          <Link to={`/${hubSlug(citySlug)}`} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">{t.rentCarIn} {cfg.cityName}</Link>
          <Link to="/become-a-host" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">{t.allResources}</Link>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">{t.ctaTitle} {cfg.cityName} {t.ctaTitle2}</h2>
          <p className="mt-4 text-blue-50 max-w-xl mx-auto">{t.ctaSub}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => track("application_started", { page: `city_host_${citySlug}_cta` })} className="px-8 py-3.5 rounded-full bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2">{t.apply} <ChevronRight size={18} /></Link>
            <a href="tel:+19782211451" className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </section>
    </div>
  );
}
